/**
 * FolderList · AI 分类页文件夹卡片网格
 *
 * 职责：
 *  · mount 时按顺序请求 /web/folder/get 和 /web/folder/statistic（复用旧 action）
 *  · 通过 adaptFolder 把旧字段对齐到 FolderListItem
 *  · 渲染自适应 Grid + FolderCard
 *  · 点卡片跳详情页 /ai-classify/folder/:folderId
 *  · 「管理 / 多选删除」模式：右上角 「管理」 按钮触发；卡片切到选择态；
 *     底部 sticky 操作条 「全选 / 已选 N / 删除」 ；删除走 batchDeleteFolders
 *
 * 数据存储策略：
 *  · 列表数据放本地 useState · 不入 Redux（首期不持久化、无跨页消费）
 *  · 管理模式开关 + 勾选集合放 Redux uiSlice（让顶部 「管理」 按钮跨组件读写）
 *  · groupID 未就绪时跳过请求，依赖 useEffect 重跑触发
 *  · loading 以"两段请求都完成"为结束信号
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Input, Menu, Modal, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderAddOutlined,
  FolderOutlined,
  SortAscendingOutlined
} from '@ant-design/icons'
import { trackClassifyClick } from '../../utils/trackClassify'
import {
  useAppDispatch,
  useAppSelector,
  useIsAdmin
} from '../../../../app/hooks'
import {
  cutFolders,
  deleteFolder,
  updateFolder
} from '../../../../actions/folder'
import {
  bumpFolderRefreshTick,
  clearRecentlyCreated,
  enterManageMode,
  exitManageMode,
  loadAIFolderList,
  markFolderEntered,
  selectActiveTasks,
  selectAllTasks,
  selectFolderListError,
  selectFolderListItems,
  selectFolderListLoading,
  selectFolderRefreshTick,
  selectFolderSort,
  selectManageMode,
  selectManageSelectedFolderIds,
  selectNewBadgeFolderIds,
  selectRecentlyCreatedFolderIds,
  selectUnclassifiedPhotoCount,
  selectViewMode,
  setFolderSort,
  setManageFolderSelection,
  toggleManageFolder
} from '../../../../slices/aiClassify'
import type { FolderListItem } from '../../../../types/ai-classify'
import {
  UNCLASSIFIED_DETAIL_ROUTE,
  UNCLASSIFIED_FOLDER_ID,
  UNCLASSIFIED_FOLDER_NAME,
  buildUnclassifiedFolder
} from './unclassifiedFolder'
import FolderCardSkeleton from './FolderCardSkeleton'
import FolderListHeader, { FolderListSort } from './FolderListHeader'
import EmptyState from './EmptyState'
import { useFolderBatchActions } from './FolderBatchActions'
import FolderListToolbar from './FolderListToolbar'
import FolderGridView from './FolderGridView'
import FolderTableView from './FolderTableView'
import styles from './FolderList.module.scss'

/** 加载态宫格骨架卡数量 · 取较大值以填满大屏；容器 overflow 裁剪、不滚动 */
const SKELETON_CARD_COUNT = 60

/**
 * 「待归档」入口开关 · 暂时隐藏列表里的待归档虚拟文件夹入口。
 * 虚拟项合成逻辑、待归档详情页（/ai-classify/unclassified）与路由都保留，
 * 需要恢复入口时把这里改回 true 即可。
 */
const SHOW_UNCLASSIFIED_ENTRY = false

/**
 * 排序比较器 · 对一组兄弟节点排序（多级展开时每级独立排序）
 *
 * 中文 / 数字 / 时间分支处理；缺字段视为 0，排在升序最前 / 降序最后
 */
function compareFolders(
  a: FolderListItem,
  b: FolderListItem,
  sort: FolderListSort
): number {
  const factor = sort.order === 'asc' ? 1 : -1
  switch (sort.key) {
    case 'folderName':
      return factor * a.folderName.localeCompare(b.folderName, 'zh-CN')
    case 'photoCount':
      return factor * (a.photoCount - b.photoCount)
    case 'subfolderCount':
      return factor * (a.subfolderCount - b.subfolderCount)
    case 'updateTime':
      return factor * ((a.updateTime || 0) - (b.updateTime || 0))
    case 'ctime':
      // 创建时间 · adaptFolder 已把 raw.ctime / raw.createTime 归一为秒数
      // 缺字段 fallback 到 updateTime，再不行视为 0（升序最前 / 降序最后）
      return (
        factor *
        ((a.ctime || a.updateTime || 0) - (b.ctime || b.updateTime || 0))
      )
    default:
      return 0
  }
}

interface FolderListProps {
  /**
   * 触发"新建文件夹"弹窗 · 由 AIClassifyPage 注入（弹窗本身在 page 层管）
   * 宫格视图右键空白菜单的「新建文件夹」项调用此回调
   */
  onCreateFolder?: () => void
}

const FolderList: React.FC<FolderListProps> = ({ onCreateFolder }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // groupID 取自 RTK group 树（pojo 直读）
  const groupID = useAppSelector((state) => state.group.groupId)
  // AI 建夹完成时由 Redux bumpFolderRefreshTick 触发刷新
  const refreshTick = useAppSelector(selectFolderRefreshTick)
  // 刚创建的 folderID 集合 · 给对应卡片打 justCreated（进场动画 ~2s 后清）
  const recentlyCreatedIds = useAppSelector(selectRecentlyCreatedFolderIds)
  // 新建未进入的 folderID 集合 · 给卡片右上角加 NEW 角标，进入后清
  const newBadgeFolderIds = useAppSelector(selectNewBadgeFolderIds)
  // 还在归档中的 folderID 集合 · 给对应卡片显示"添加中..."徽标
  const activeTasks = useAppSelector(selectActiveTasks)
  const archivingFolderIds = useMemo(
    () => new Set(activeTasks.map((t) => String(t.folderID))),
    [activeTasks]
  )
  // timeout 状态的 folderID（archiving 超时后转 timeout，photoBook 后台已扫完
  // 但 rule 没匹配到照片）。FolderCard 据此展示灰色"未匹配到照片"徽标，让用户
  // 知道扫描结束了，避免一直显示"添加中"误以为卡住。
  const allTasks = useAppSelector(selectAllTasks)
  const timeoutFolderIds = useMemo(
    () =>
      new Set(
        allTasks
          .filter((t) => t.status === 'timeout')
          .map((t) => String(t.folderID))
      ),
    [allTasks]
  )
  // 多选模式（删除 / 下载共用同一套选择，底部操作条同时给两个按钮）
  const manageMode = useAppSelector(selectManageMode)
  const selectedFolderIds = useAppSelector(selectManageSelectedFolderIds)
  // 视图：grid（默认）/ list；list 多展示一列「最后更新时间」
  const viewMode = useAppSelector(selectViewMode)

  const isAdmin = useIsAdmin()

  // 列表数据源已提升到 aiClassify/folderList slice · 组件只读，不再本地持有
  const folders = useAppSelector(selectFolderListItems)
  /**
   * 未归类（待归档）照片数 · 来自 folderList slice（/web/folder/ai/get 根请求）。
   * 仅当 folders 非空且本值 > 0 时，在列表末尾合成「待归档」虚拟项。
   */
  const unclassifiedCount = useAppSelector(selectUnclassifiedPhotoCount)
  const loading = useAppSelector(selectFolderListLoading)
  const error = useAppSelector(selectFolderListError)
  /**
   * 文件夹批量/单条操作（下载 / 移动 / 删除）· 统一由 FolderBatchActionsProvider 提供
   * 这里只取右键菜单要用的 actions；下载/删除进行中状态（downloading/deleting）由
   * FolderListToolbar 自行从同一 Provider 取，无需在本组件透传。
   */
  const { batchDownload, singleDownload, batchMove, singleMove, batchDelete } =
    useFolderBatchActions()
  /** 右键「重命名」目标 · 非空时打开重命名 Modal */
  const [renameTarget, setRenameTarget] = useState<FolderListItem | null>(null)
  /** 重命名 Modal 内的输入值 · 受控 */
  const [renameValue, setRenameValue] = useState('')
  const [renaming, setRenaming] = useState(false)
  /** 列表视图的当前排序 · 已上提到 redux uiSlice，供头部「排序」按钮 + 列表表头 + 右键菜单共用 */
  const listSort = useAppSelector(selectFolderSort)
  const setListSort = useCallback(
    (next: FolderListSort) => {
      dispatch(setFolderSort(next))
    },
    [dispatch]
  )
  const containerRef = useRef<HTMLDivElement>(null)
  /**
   * 宫格视图右键空白展开菜单 · 仅 grid 视图启用
   *  · null = 未触发；非空 = 在 viewport (x,y) 处弹菜单
   *  · 菜单 portal 到 body，按这里的 (x,y) 用 position:fixed 手动定位
   */
  const [emptyMenuPos, setEmptyMenuPos] = useState<{
    x: number
    y: number
  } | null>(null)

  /**
   * 拖拽移动 state · HTML5 native drag-and-drop
   *  · draggedIds 非空表示有拖动会话进行中（来源可能是单卡 / 多选组）
   *  · dragOverFolderId 是当前 hover 在其上的目标 folderId（视觉高亮 + 决定 drop 命中）
   *  · 与原生 dataTransfer 双轨：state 用于 React 渲染，dataTransfer 用于让浏览器
   *    认为这是有效的 drag 会话（必须 setData 否则部分浏览器不触发 drop）
   */
  const [draggedIds, setDraggedIds] = useState<string[] | null>(null)
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null)

  /**
   * mount / 切团队（groupID）/ 刷新（refreshTick）时拉取顶层列表。
   * 数据写入 folderList slice，竞态由 slice 的 requestId 兜底；组件只负责触发。
   */
  useEffect(() => {
    if (groupID) dispatch(loadAIFolderList({ groupID }))
    // refreshTick 进 deps：bumpFolderRefreshTick 后重拉一次
  }, [dispatch, groupID, refreshTick])

  // 进场动画播完后清空 recentlyCreatedIds，避免后续重渲染再播一次
  useEffect(() => {
    if (recentlyCreatedIds.length === 0) return
    const t = window.setTimeout(() => {
      dispatch(clearRecentlyCreated())
    }, 2000) // 动画 480ms + 高亮 1.4s = 1.88s，2s 兜底
    return () => clearTimeout(t)
  }, [recentlyCreatedIds, dispatch])

  // 列表数据变化（如刚刷新完）时，把已被删/不存在的 id 从勾选集里清掉
  // 避免管理模式中残留过期勾选导致计数不一致
  useEffect(() => {
    if (!manageMode || selectedFolderIds.length === 0) return
    const aliveIds = new Set(folders.map((f) => f.folderId))
    const filtered = selectedFolderIds.filter((id) => aliveIds.has(id))
    if (filtered.length !== selectedFolderIds.length) {
      dispatch(setManageFolderSelection(filtered))
    }
  }, [folders, manageMode, selectedFolderIds, dispatch])

  // ────────────── 卡片点击 ──────────────

  const handleCardClick = useCallback(
    (folderId: string) => {
      // 待归档虚拟项：始终进详情（不参与多选），走独立路由
      if (folderId === UNCLASSIFIED_FOLDER_ID) {
        trackClassifyClick('enterOne', { folderName: UNCLASSIFIED_FOLDER_NAME })
        navigate(UNCLASSIFIED_DETAIL_ROUTE)
        return
      }
      const folderName =
        folders.find((f) => f.folderId === folderId)?.folderName || ''
      if (manageMode) {
        trackClassifyClick('selectOne', { folderName })
        dispatch(toggleManageFolder(folderId))
      } else {
        trackClassifyClick('enterOne', { folderName })
        dispatch(markFolderEntered({ folderId }))
        navigate('/ai-classify/folder/' + folderId)
      }
    },
    [dispatch, manageMode, folders]
  )

  /**
   * checkbox 单独点击 · 与整张卡 onClick 区分（stopPropagation 已在子组件）
   *  · 非 manageMode：进入 manageMode + toggle 这条（"hover 出 checkbox 一点就进多选"）
   *  · manageMode：仅 toggle（与 onClick 行为一致，但 checkbox 命中区更清晰）
   */
  const handleSelectClick = useCallback(
    (folderId: string) => {
      const folderName =
        folders.find((f) => f.folderId === folderId)?.folderName || ''
      trackClassifyClick('selectOne', { folderName })
      if (!manageMode) dispatch(enterManageMode())
      dispatch(toggleManageFolder(folderId))
    },
    [dispatch, manageMode, folders]
  )

  const handleRetry = useCallback(() => {
    if (groupID) dispatch(loadAIFolderList({ groupID }))
  }, [dispatch, groupID])

  // ────────────── 列表视图：展开 / 收起子文件夹 ──────────────

  /**
   * 顶层 folders 排序结果 · 宫格视图直接 map 这一份
   *  · 单独抽 useMemo 让 grid 视图也吃到 listSort（之前 grid 直接用 folders 跳过了排序）
   */
  const sortedTopFolders = useMemo<FolderListItem[]>(
    () => [...folders].sort((a, b) => compareFolders(a, b, listSort)),
    [folders, listSort]
  )

  /**
   * 「待归档」虚拟项 · 只要文件夹列表非空就合成，固定置于列表末尾。
   * （feat/0614：后端不再下发待归档数量，不依赖照片张数；卡片/行也不展示张数）
   *
   * 不进入真实 folders，因此不参与多选 / 全选 / 框选 / 拖拽 / 批量删除；
   * grid / list 视图都在各自 map 之后单独渲染它（精简 props、不挂 Dropdown）。
   *
   * 当前由 SHOW_UNCLASSIFIED_ENTRY 关掉：恒返回 null，入口隐藏（合成逻辑保留）。
   */
  const unclassifiedFolder = useMemo<FolderListItem | null>(
    () =>
      SHOW_UNCLASSIFIED_ENTRY && folders.length > 0
        ? buildUnclassifiedFolder(unclassifiedCount)
        : null,
    [folders.length, unclassifiedCount]
  )

  // ────────────── 右键菜单：单条 重命名 / 删除 / 下载 ──────────────

  /** 打开重命名 Modal · 选中要改的 folder 记到 state，input 预填当前名字 */
  const handleSingleRenameStart = useCallback((folder: FolderListItem) => {
    setRenameTarget(folder)
    setRenameValue(folder.folderName)
  }, [])

  /** 确认重命名 · dispatch updateFolder 后刷新列表，关闭 Modal */
  const handleSingleRenameConfirm = useCallback(() => {
    const trimmed = renameValue.trim()
    if (!renameTarget || !groupID) return
    if (!trimmed) {
      message.warning('文件夹名不能为空')
      return
    }
    if (trimmed === renameTarget.folderName) {
      // 没变就直接关弹窗 · 避免无效请求
      setRenameTarget(null)
      return
    }
    setRenaming(true)
    Promise.resolve(
      dispatch(
        updateFolder({
          groupID,
          folderID: Number(renameTarget.folderId),
          folderName: trimmed
        })
      )
    )
      .then(() => {
        message.success('重命名成功')
        dispatch(bumpFolderRefreshTick({}))
        setRenameTarget(null)
      })
      .catch(() => {
        message.error('重命名失败，请稍后重试')
      })
      .finally(() => {
        setRenaming(false)
      })
  }, [renameValue, renameTarget, groupID, dispatch])

  /** 单条删除 · 与 photoFolders/FolderList:170 同款 Modal.confirm */
  const handleSingleDelete = useCallback(
    (folder: FolderListItem) => {
      if (!groupID) return
      Modal.confirm({
        title: `确定删除「${folder.folderName}」？`,
        content:
          '文件夹删除后不可恢复，文件夹内的照片不会被删除，会保留在【全部照片】中',
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          return Promise.resolve(
            dispatch(
              deleteFolder({
                folderID: Number(folder.folderId),
                groupID
              })
            )
          )
            .then(() => {
              message.success('已删除')
              dispatch(bumpFolderRefreshTick({}))
            })
            .catch(() => {
              message.error('删除失败，请稍后重试')
            })
        }
      })
    },
    [groupID, dispatch]
  )

  // ────────────── 宫格视图右键空白菜单 ──────────────

  /**
   * 容器右键 · 仅 grid 视图 + 起点不在卡片上时展开"新建文件夹 / 排序"菜单
   *
   * list 视图不开启：
   *  · list 行铺满宽度，"空白"主要在最后一行下方，命中率低
   *  · list 视图已有表头排序按钮，菜单价值不大
   */
  const handleContainerContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (viewMode !== 'grid') return
      const target = e.target as HTMLElement
      // 卡片 / 排序表头自身已有右键菜单，不抢
      if (target.closest('[data-folder-id]')) return
      if (target.closest('[data-no-box-select]')) return
      e.preventDefault()
      setEmptyMenuPos({ x: e.clientX, y: e.clientY })
    },
    [viewMode]
  )

  /**
   * 空白菜单全局关闭监听 · 点击 / 右键 / 滚动 / Esc 都关掉
   *
   * antd 3 Dropdown 在 trigger=[] 时不自带 clickOutside 检测，要手动加。
   * 用 mousedown 而非 click —— 在 mousedown 阶段就关菜单，避免被 click 阶段
   * 其他控件抢走。capture 阶段（第三参数 true）让我们先于子组件接到事件。
   *
   * 监听在下一个 event loop tick 才挂 · 避免"打开菜单的 contextmenu 事件"
   * 被同一个 useEffect 立即接住后又把菜单关掉。
   */
  useEffect(() => {
    if (!emptyMenuPos) return
    const isInsideMenu = (e: Event): boolean => {
      const t = e.target as HTMLElement | null
      if (!t) return false
      // 空白菜单 portal 渲染 antd Menu（.ant-menu）；「排序」子菜单浮层
      // 单独 portal 到 body，仍是 .ant-menu，故一个判定覆盖主菜单 + 子菜单
      return !!t.closest('.ant-menu')
    }
    const onPointer = (e: MouseEvent): void => {
      if (isInsideMenu(e)) return
      setEmptyMenuPos(null)
    }
    const onContext = (e: MouseEvent): void => {
      if (isInsideMenu(e)) return
      setEmptyMenuPos(null)
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setEmptyMenuPos(null)
    }
    const onScroll = (): void => setEmptyMenuPos(null)
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointer, true)
      document.addEventListener('contextmenu', onContext, true)
      document.addEventListener('keydown', onKey, true)
      window.addEventListener('scroll', onScroll, true)
    }, 0)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('mousedown', onPointer, true)
      document.removeEventListener('contextmenu', onContext, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!emptyMenuPos])

  /** 排序菜单项的"按 X (升 / 降)"派生
   *  · 列表 / 宫格视图、头部排序按钮、空白右键菜单全部复用同一份
   *  · 创建时间 ctime 由后端 raw.ctime / raw.createTime 下发，adaptFolder.ts 已归一 */
  const sortMenuItems = useMemo(
    () => [
      { key: 'folderName:asc', label: '按文件夹名 升序' },
      { key: 'folderName:desc', label: '按文件夹名 降序' },
      { key: 'photoCount:desc', label: '按照片数 从多到少' },
      { key: 'photoCount:asc', label: '按照片数 从少到多' },
      { key: 'subfolderCount:desc', label: '按子文件夹数 从多到少' },
      { key: 'subfolderCount:asc', label: '按子文件夹数 从少到多' },
      { key: 'updateTime:desc', label: '按更新时间 从近到远' },
      { key: 'updateTime:asc', label: '按更新时间 从远到近' },
      { key: 'ctime:desc', label: '按创建时间 从近到远' },
      { key: 'ctime:asc', label: '按创建时间 从远到近' }
    ],
    []
  )

  /**
   * 空白右键菜单内容
   *  · 「新建文件夹」管理员可见（onCreateFolder 由 AIClassifyPage 注入）
   *  · 「排序」SubMenu 8 项，与 FolderListHeader 列对齐 + 升降双向
   */
  const emptyAreaMenu = useMemo(
    () => (
      <Menu
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation()
          setEmptyMenuPos(null)
          if (key === 'create-folder') {
            onCreateFolder && onCreateFolder()
            return
          }
          if (typeof key === 'string' && key.startsWith('sort:')) {
            const [, sortKey, order] = key.split(':')
            setListSort({
              key: sortKey as FolderListSort['key'],
              order: order as FolderListSort['order']
            })
          }
        }}
      >
        {onCreateFolder && (
          <Menu.Item key="create-folder">
            <FolderAddOutlined /> 新建文件夹
          </Menu.Item>
        )}
        {onCreateFolder && <Menu.Divider />}
        <Menu.SubMenu
          key="sort"
          title={
            <>
              <SortAscendingOutlined /> 排序
            </>
          }
        >
          {sortMenuItems.map((item) => (
            <Menu.Item key={`sort:${item.key}`}>
              {`${listSort.key}:${listSort.order}` === item.key && (
                <CheckOutlined style={{ color: '#6366f1', marginRight: 4 }} />
              )}
              {item.label}
            </Menu.Item>
          ))}
        </Menu.SubMenu>
      </Menu>
    ),
    [onCreateFolder, sortMenuItems, listSort]
  )

  // ────────────── 鼠标拖框选 ──────────────

  /**
   * 框选会话进行中标记 · 拖动中选区可能瞬间为空（拖回空白），用它挂起"选中清空 →
   * 自动退出多选"那个 effect，避免框选途中被踢出多选、闪烁。
   */
  const isBoxSelectingRef = useRef(false)
  /** 最近一次实时命中 · 框选结束时据它判断空选 → 退出多选 */
  const latestBoxIdsRef = useRef<string[]>([])

  /**
   * 框选开始（越过阈值）· 进入多选 + 标记会话进行中
   * enterManageMode 会清空旧选，等同于"重新拉一个框替换之前的选择"
   */
  const handleBoxSelectStart = useCallback(() => {
    isBoxSelectingRef.current = true
    latestBoxIdsRef.current = []
    dispatch(enterManageMode())
  }, [dispatch])

  /**
   * 框选实时命中回调 · 拖动 / 滚动中 useBoxSelect 按帧节流上抛当前框中的 folderId
   * 列表（含空集）。这里整体替换选中即可——进入多选已在 handleBoxSelectStart 做过，
   * 空选的退出收尾留给 handleBoxSelectEnd。
   */
  const handleBoxSelect = useCallback(
    (ids: string[]) => {
      latestBoxIdsRef.current = ids
      dispatch(setManageFolderSelection(ids))
    },
    [dispatch]
  )

  /**
   * 框选结束（松手 / Esc / 失焦）· 解除"进行中"标记；最终为空选则退出多选
   * （标记一解除，下方"选中清空自动退出"effect 才重新生效）
   */
  const handleBoxSelectEnd = useCallback(() => {
    isBoxSelectingRef.current = false
    if (latestBoxIdsRef.current.length === 0) dispatch(exitManageMode())
  }, [dispatch])

  /**
   * 框选空白单击回调 · 起点在空白、未拖动时 BoxSelect 触发
   * 多选模式下视为"点空白退出多选"（起点已由 hook 排除卡片 / data-no-box-select 区）
   */
  const handleBoxEmptyClick = useCallback(() => {
    if (manageMode) dispatch(exitManageMode())
  }, [manageMode, dispatch])

  // ────────────── 移动到文件夹 ──────────────

  /**
   * 拖拽移动 · 直接 cutFolders + 弹 antd Modal 二次确认
   * @param targetFolder 拖入目标 folder · 必须不是被拖的某一个
   * @param ids 被拖的 folderId 列表（dragstart 时确定）
   */
  const performDragMove = useCallback(
    (targetFolder: FolderListItem, ids: string[]) => {
      if (!groupID || ids.length === 0) return
      // 自身防护：目标在被拖列表里 → 早退
      if (ids.indexOf(targetFolder.folderId) >= 0) return
      Modal.confirm({
        title: `把 ${ids.length} 个文件夹移动到「${targetFolder.folderName}」中？`,
        content: '移动后文件夹将作为目标的子文件夹。',
        okText: '移动',
        cancelText: '取消',
        onOk: () => {
          const folderIDsNumeric = ids
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n))
          return dispatch(
            cutFolders({
              groupID,
              folderIDs: folderIDsNumeric,
              dstParentFolderID: Number(targetFolder.folderId),
              source: 5
            })
          )
            .then(() => {
              message.success(`已移动 ${ids.length} 个文件夹`)
              dispatch(bumpFolderRefreshTick({}))
              if (manageMode) dispatch(exitManageMode())
            })
            .catch(() => {
              // cutFolders 内部已弹 message.error，这里无需额外处理
            })
        }
      })
    },
    [groupID, dispatch, manageMode]
  )

  /**
   * 拖拽 dragstart · 决定本次拖什么：
   *  · 多选模式 + 当前卡片已勾选 → 拖整组 selectedFolderIds（批量移动）
   *  · 否则 → 仅拖被按下的这一张
   *
   * 仅 admin 可拖；非 admin preventDefault 阻止 native drag 启动。
   */
  const handleCardDragStart = useCallback(
    (e: React.DragEvent<HTMLElement>, folder: FolderListItem) => {
      if (!isAdmin) {
        e.preventDefault()
        return
      }
      const ids =
        manageMode && selectedFolderIds.indexOf(folder.folderId) >= 0
          ? [...selectedFolderIds]
          : [folder.folderId]
      setDraggedIds(ids)
      e.dataTransfer.effectAllowed = 'move'
      try {
        // 必须 setData 否则部分浏览器不会触发 drop；内容仅给浏览器看，业务逻辑走 React state
        e.dataTransfer.setData('text/plain', ids.join(','))
      } catch {
        /* noop */
      }
    },
    [isAdmin, manageMode, selectedFolderIds]
  )

  const handleCardDragEnd = useCallback(() => {
    setDraggedIds(null)
    setDragOverFolderId(null)
  }, [])

  /**
   * 拖到目标卡上 · 必须 preventDefault 才能让浏览器允许 drop
   * 自己拖到自己（或被拖列表里的兄弟）不响应
   */
  const handleCardDragOver = useCallback(
    (e: React.DragEvent<HTMLElement>, folder: FolderListItem) => {
      if (!draggedIds || draggedIds.indexOf(folder.folderId) >= 0) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverFolderId((prev) =>
        prev === folder.folderId ? prev : folder.folderId
      )
    },
    [draggedIds]
  )

  const handleCardDragLeave = useCallback(
    (_e: React.DragEvent<HTMLElement>, folder: FolderListItem) => {
      setDragOverFolderId((prev) => (prev === folder.folderId ? null : prev))
    },
    []
  )

  const handleCardDrop = useCallback(
    (e: React.DragEvent<HTMLElement>, folder: FolderListItem) => {
      e.preventDefault()
      const ids = draggedIds
      setDraggedIds(null)
      setDragOverFolderId(null)
      if (!ids || ids.indexOf(folder.folderId) >= 0) return
      performDragMove(folder, ids)
    },
    [draggedIds, performDragMove]
  )

  /**
   * 构造右键菜单 · 单条 / 批量两种形态
   *
   * 形态切换由"当前是否多选 + 右键的卡是否在选中集合内"决定：
   *  · 非多选：单条菜单（下载 / 重命名 / 删除）—— 仅作用于被右键的这张卡
   *  · 多选 + 右键的卡在选中集合内：批量菜单（下载 N 项 / 删除 N 项）
   *  · 多选 + 右键的卡不在选中集合：上层 Dropdown 禁用，本函数不会被触发
   *
   * 权限：
   *  · 单条 重命名 / 删除 走 isAdmin
   *  · 批量 删除 也只对 isAdmin 显示，与底部操作条对齐
   *  · 下载在两种形态都对所有人开（后端自有权限校验）
   */
  const buildFolderContextMenu = useCallback(
    (folder: FolderListItem) => {
      const isBatch =
        manageMode && selectedFolderIds.indexOf(folder.folderId) >= 0
      if (isBatch) {
        const count = selectedFolderIds.length
        return (
          <Menu
            onClick={({ key, domEvent }) => {
              domEvent.stopPropagation()
              switch (key) {
                case 'batch-download':
                  batchDownload()
                  break
                case 'batch-move':
                  batchMove()
                  break
                case 'batch-delete':
                  batchDelete()
                  break
                default:
                  break
              }
            }}
          >
            <Menu.Item key="batch-download">
              <DownloadOutlined /> 下载（{count} 项）
            </Menu.Item>
            {isAdmin && (
              <Menu.Item key="batch-move">
                <FolderOutlined /> 移动到（{count} 项）
              </Menu.Item>
            )}
            {isAdmin && <Menu.Divider />}
            {isAdmin && (
              <Menu.Item key="batch-delete" style={{ color: '#ef4444' }}>
                <DeleteOutlined /> 删除（{count} 项）
              </Menu.Item>
            )}
          </Menu>
        )
      }
      return (
        <Menu
          onClick={({ key, domEvent }) => {
            // antd Menu 的 domEvent 是原生 Event；stopPropagation 通用
            domEvent.stopPropagation()
            switch (key) {
              case 'rename':
                handleSingleRenameStart(folder)
                break
              case 'move':
                singleMove(folder)
                break
              case 'delete':
                handleSingleDelete(folder)
                break
              case 'download':
                singleDownload(folder)
                break
              default:
                break
            }
          }}
        >
          <Menu.Item key="download">
            <DownloadOutlined /> 下载
          </Menu.Item>
          <Menu.Item key="rename" disabled={!isAdmin}>
            <EditOutlined /> 重命名
          </Menu.Item>
          <Menu.Item key="move" disabled={!isAdmin}>
            <FolderOutlined /> 移动到
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            key="delete"
            disabled={!isAdmin}
            style={{ color: '#ef4444' }}
          >
            <DeleteOutlined /> 删除
          </Menu.Item>
        </Menu>
      )
    },
    [
      manageMode,
      selectedFolderIds,
      isAdmin,
      handleSingleRenameStart,
      singleMove,
      handleSingleDelete,
      singleDownload,
      batchDownload,
      batchMove,
      batchDelete
    ]
  )

  // 列表数据为空且当前在管理模式 → 直接退出管理模式（没有可操作对象）
  // 仅在"加载完成且 folders 真为空"时触发，避免初始 loading 时误退
  useEffect(() => {
    if (manageMode && !loading && folders.length === 0) {
      dispatch(exitManageMode())
    }
  }, [manageMode, loading, folders.length, dispatch])

  // 多选模式 + 选中清空 → 自动退出多选
  //
  // 统一收尾以下路径，避免出现"管理态视觉还在但没有任何选中"的中间态：
  //  · 反勾最后一张（handleSelectClick / handleCardClick / 批量右键菜单内单条 toggle）
  //  · 取消全选（handleToggleSelectAll 的 setManageFolderSelection([]) 分支）
  //  · 列表刷新后所有已选 id 失效，被上方过滤死 id 的 effect 设为空数组
  //
  // 入场路径不会被误退出：handleSelectClick / handleToggleSelectAll 在 React 16
  // 合成事件回调内同一 batch 提交，render 时 selection 已非空；框选进行中则由
  // isBoxSelectingRef 挂起本 effect，结束时再由 handleBoxSelectEnd 收尾空选
  useEffect(() => {
    // 框选进行中：拖动途中选区可能瞬间为空，此时不退出，交给 handleBoxSelectEnd 收尾
    if (isBoxSelectingRef.current) return
    if (manageMode && selectedFolderIds.length === 0) {
      dispatch(exitManageMode())
    }
  }, [manageMode, selectedFolderIds, dispatch])

  // 多选态下按 Esc → 取消全部选择（退出多选）· 仅 manageMode 时挂监听
  //
  // 这是 FolderList 的业务键盘快捷键，与 useBoxSelect 内"框选会话中按 Esc 取消正在
  // 拉的框"是两回事：那个只在拖框进行时生效、属框选机制；这个针对"已选中、静止态"，
  // 属多选生命周期，所以放在业务层而非 hook 里。
  //
  // 谦让规则：焦点在输入框、或有 antd 弹层（重命名 Modal / 批量删除 confirm 等）打开
  // 时跳过，让它们各自处理 Esc（关弹窗 / 清输入），避免误清选择。判断用事件 target
  // 的归属：antd 弹窗打开会把焦点 trap 在弹层内，此时 Esc 的 target 落在弹层节点中。
  useEffect(() => {
    if (!manageMode) return
    const handleEscClearSelection = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      const target = e.target as HTMLElement | null
      if (
        target &&
        target.closest(
          'input, textarea, [contenteditable="true"], .ant-modal, .ant-modal-confirm'
        )
      ) {
        return
      }
      dispatch(exitManageMode())
    }
    window.addEventListener('keydown', handleEscClearSelection)
    return () => window.removeEventListener('keydown', handleEscClearSelection)
  }, [manageMode, dispatch])

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onContextMenu={handleContainerContextMenu}
    >
      {/* 加载态 · 宫格骨架屏（绝对覆盖 + overflow 裁剪：多卡填满大屏，容器不滚动） */}
      {loading && (
        <div className={styles.skeletonViewport} aria-hidden="true">
          <div className={styles.grid}>
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
              <FolderCardSkeleton key={i} />
            ))}
          </div>
        </div>
      )}

      {!loading && error && (
        <div className={styles.stateBox}>
          <div className={styles.errorText}>
            <span>{error}</span>
            <Button onClick={handleRetry}>点击重试</Button>
          </div>
        </div>
      )}

      {!loading && !error && folders.length === 0 && (
        <EmptyState onCreateFolder={onCreateFolder} />
      )}

      {/* 列表工具栏 · 宫格 / 列表两视图共用 · 连接型零-props 组件，自取 Redux
          选择 / 视图状态并自派发，批量操作走 useFolderBatchActions()。
          容器自带 role="toolbar" + data-no-box-select（见 FolderListToolbar）
          工具栏在 viewport 之外，滚动时固定在顶部（sticky top:0 由 FolderListToolbar 自管） */}
      {!loading && !error && folders.length > 0 && <FolderListToolbar />}

      {/* viewport · 工具栏下方主体区，flex:1 撑满剩余高度；各视图在此内部各自滚动 */}
      {!loading && !error && folders.length > 0 && (
        <div className={styles.viewport}>
          {viewMode === 'grid' && (
            <FolderGridView
              folders={sortedTopFolders}
              unclassifiedFolder={unclassifiedFolder}
              buildFolderContextMenu={buildFolderContextMenu}
              manageMode={manageMode}
              selectedFolderIds={selectedFolderIds}
              isAdmin={isAdmin}
              recentlyCreatedIds={recentlyCreatedIds}
              newBadgeFolderIds={newBadgeFolderIds}
              archivingFolderIds={archivingFolderIds}
              timeoutFolderIds={timeoutFolderIds}
              draggedIds={draggedIds}
              dragOverFolderId={dragOverFolderId}
              onCardClick={handleCardClick}
              onSelectClick={handleSelectClick}
              onCardDragStart={handleCardDragStart}
              onCardDragEnd={handleCardDragEnd}
              onCardDragOver={handleCardDragOver}
              onCardDragLeave={handleCardDragLeave}
              onCardDrop={handleCardDrop}
              boxSelectContainerRef={containerRef}
              onBoxSelect={handleBoxSelect}
              onBoxSelectStart={handleBoxSelectStart}
              onBoxSelectEnd={handleBoxSelectEnd}
              onBoxEmptyClick={handleBoxEmptyClick}
            />
          )}

          {viewMode === 'list' && (
            <FolderTableView
              folders={sortedTopFolders}
              unclassifiedFolder={unclassifiedFolder}
              listSort={listSort}
              setListSort={setListSort}
              buildFolderContextMenu={buildFolderContextMenu}
              manageMode={manageMode}
              selectedFolderIds={selectedFolderIds}
              archivingFolderIds={archivingFolderIds}
              timeoutFolderIds={timeoutFolderIds}
              isAdmin={isAdmin}
              onRowClick={handleCardClick}
              onSelectClick={handleSelectClick}
              singleDownload={singleDownload}
              handleSingleRenameStart={handleSingleRenameStart}
              handleSingleDelete={handleSingleDelete}
            />
          )}
        </div>
      )}

      {/* 重命名 Modal · 由右键菜单"重命名"触发 */}
      <Modal
        open={!!renameTarget}
        title="重命名文件夹"
        okText="确认"
        cancelText="取消"
        confirmLoading={renaming}
        onOk={handleSingleRenameConfirm}
        onCancel={() => setRenameTarget(null)}
        destroyOnHidden
      >
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder="请输入新的文件夹名"
          maxLength={32}
          onPressEnter={handleSingleRenameConfirm}
          autoFocus
        />
      </Modal>

      {/* 宫格右键空白菜单 · 直接 portal 到 body 固定在光标处渲染。
          不走 antd Dropdown 自动定位 —— antd6 rc-trigger 对 position:fixed 0×0
          锚点会算出错误坐标（popup 跑出视口外），这里手动定位最稳。
          点外部 / 右键 / 滚动 / Esc 关闭由上方全局 useEffect 统一处理。 */}
      {emptyMenuPos &&
        createPortal(
          <div
            className={styles.emptyMenuPopup}
            style={{
              position: 'fixed',
              left: emptyMenuPos.x,
              top: emptyMenuPos.y,
              zIndex: 1050
            }}
          >
            {emptyAreaMenu}
          </div>,
          document.body
        )}
    </div>
  )
}

export default FolderList
